import clsx from "clsx";
import React, { Fragment, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useSearch } from "wouter/use-location";

import { useBuildComparison } from "../../../queries/useBuildComparison";
import { createHashStateHook } from "../../../stores/useHashState";
import { KeyValue } from "../../../ui/KeyValue/KeyValue";
import { LinearProgress } from "../../../ui/LinearProgress";
import { Description } from "../Common/Description";
import { TirePartDetails } from "../Common/TirePartDetails";
import { Warranty, hasWarranty } from "../Common/Warranty";
import { partIsStaggered } from "../Results/Filters/TireSizeFilters/partIsStaggered";
import { AddToQuote } from "./AddToQuote";
import { AdditionalCosts } from "./AdditionalCosts";
import { GrandTotal } from "./GrandTotal";
import { OtherInformtation, hasOtherInfo } from "./OtherInformtation";
import { PricingDetails } from "./PricingDetails";
import { ProductInfo } from "./ProductInfo";
import { Promotion, hasPropmotions } from "./Promotion";
import { SeasonInfo } from "./SeasonInfo";
import { Subtotal } from "./Subtotal";
import { Tax } from "./Tax";

export const compareTiresHash = "#compare-tires";
export const useCompareHash = createHashStateHook(compareTiresHash);

const rowCaptionClass = "font-medium min-w-[8rem] print:min-w-[6rem] pr-4";
export const gridCellClass = "min-w-[12rem] pr-4";

type TireComparisonGridProps = {
  compareIds: string[];
  printMode: boolean;
};

export const TireComparisonGrid: FunctionComponent<TireComparisonGridProps> = ({
  compareIds,
  printMode,
}) => {
  const search = useSearch();
  const queryParams = new URLSearchParams(search);

  const { t } = useTranslation();
  const { data, isLoading } = useBuildComparison(compareIds, {
    keepPreviousData: true,
  });

  const visibleResults = data?.compareResults.filter(
    ({ part }) =>
      compareIds.length === 0 ||
      compareIds.includes(part.partDetails.primaryPart.itemId)
  );
  const gridTemplateHeaderColumns = `1fr ${visibleResults
    ?.map(() => "2fr")
    .join(" ")}`;
  const gridTemplateColumns = `${visibleResults?.map(() => "1fr").join(" ")}`;
  const gridColumn = `span ${visibleResults?.length || 1}`;
  return (
    <div
      className={clsx(
        "grid",
        printMode ? "text-xs overflow-hidden" : "overflow-x-auto"
      )}
    >
      <div className={clsx(!printMode && "inline-block")}>
        {/* Product info */}
        <section
          className="grid gap-2 items-end"
          id="product-info-section"
          style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
        >
          <h3 className={clsx("md:text-[1.16em]", rowCaptionClass)}>
            {t("compare.productInformation")}:
          </h3>
          {visibleResults?.map(({ part }) => (
            <ProductInfo
              key={part.partDetails.primaryPart.itemId}
              part={part}
            />
          ))}
        </section>
        {!data || isLoading ? (
          <LinearProgress className="h-0.5" />
        ) : (
          <hr className="my-[0.05rem]" />
        )}
        {Boolean(visibleResults?.length) && (
          <>
            {/* Product details */}
            <section
              className="grid gap-2 items-center mt-4 md:mt-8"
              id="product-details-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>
                {t("compare.productDetails")}:
              </h4>
              {Boolean(visibleResults?.length) && (
                <div
                  className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                  style={{ gridColumn, gridTemplateColumns }}
                >
                  {visibleResults?.map(({ part }) => {
                    const isStaggered = partIsStaggered(part);
                    return (
                      <Fragment key={part.partDetails.primaryPart.itemId}>
                        <TirePartDetails
                          className={gridCellClass}
                          isFront={isStaggered}
                          isRear={false}
                          part={part.partDetails.primaryPart}
                        />
                        {isStaggered && (
                          <TirePartDetails
                            className={gridCellClass}
                            isFront={false}
                            isRear={true}
                            part={part.partDetails.secondaryParts[0]}
                          />
                        )}
                      </Fragment>
                    );
                  })}
                </div>
              )}
            </section>
            {/* Season info */}
            <section
              className="grid gap-2 items-center mt-2"
              id="season-info-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>{t("compare.season")}:</h4>
              {Boolean(visibleResults?.length) && (
                <div
                  className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                  style={{ gridColumn, gridTemplateColumns }}
                >
                  {visibleResults?.map(({ part }) => (
                    <SeasonInfo
                      key={part.partDetails.primaryPart.itemId}
                      part={part}
                    />
                  ))}
                </div>
              )}
            </section>
            {/* Description */}
            <section
              className="grid print:hidden gap-2 items-center mt-2"
              id="description-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>{t("compare.description")}:</h4>
              {Boolean(visibleResults?.length) && (
                <div
                  className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                  style={{ gridColumn, gridTemplateColumns }}
                >
                  {visibleResults?.map(({ part }) => (
                    <Description
                      className={gridCellClass}
                      key={part.partDetails.primaryPart.itemId}
                      part={part}
                    />
                  ))}
                </div>
              )}
            </section>
            {/* Warranty */}
            {hasWarranty(visibleResults) && (
              <section
                className="grid print:hidden gap-2 items-center mt-2"
                id="warranty-section"
                style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
              >
                <h4 className={rowCaptionClass}>{t("compare.warranty")}:</h4>
                {Boolean(visibleResults?.length) && (
                  <div
                    className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                    style={{ gridColumn, gridTemplateColumns }}
                  >
                    {visibleResults?.map(({ part }) => (
                      <Warranty
                        className={gridCellClass}
                        key={part.partDetails.primaryPart.itemId}
                        part={part}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}
            {/* Other Information */}
            {hasOtherInfo(visibleResults) && (
              <section
                className="grid print:hidden gap-2 items-center mt-2"
                id="other-information-section"
                style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
              >
                <h4 className={rowCaptionClass}>
                  {t("compare.otherInformation")}:
                </h4>
                {Boolean(visibleResults?.length) && (
                  <div
                    className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                    style={{ gridColumn, gridTemplateColumns }}
                  >
                    {visibleResults?.map(({ part }) => (
                      <OtherInformtation
                        key={part.partDetails.primaryPart.itemId}
                        part={part}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}

            <section
              className="grid gap-2 mt-8 print:hidden"
              id="pricing-header-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h3 className={clsx("md:text-[1.16em]", rowCaptionClass)}>
                {t("compare.pricingInformation")}:
              </h3>
            </section>
            <hr className="my-1 print:hidden" />
            {/* Pricing Details */}
            <section
              className="grid gap-2 items-center mt-2"
              id="pricing-details-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>
                {t("compare.pricingDetails")}:
              </h4>
              {Boolean(visibleResults?.length) && (
                <div
                  className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                  style={{ gridColumn, gridTemplateColumns }}
                >
                  {visibleResults?.map(({ part }) => (
                    <PricingDetails
                      isStaggered={partIsStaggered(part)}
                      key={part.partDetails.primaryPart.itemId}
                      part={part}
                    />
                  ))}
                </div>
              )}
            </section>
            {/* Promotion */}
            {hasPropmotions(visibleResults) && (
              <section
                className="grid gap-2 items-center mt-2"
                id="promotion-section"
                style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
              >
                <h4 className={rowCaptionClass}>{t("compare.promotion")}:</h4>
                {Boolean(visibleResults?.length) && (
                  <div
                    className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                    style={{ gridColumn, gridTemplateColumns }}
                  >
                    {visibleResults?.map(({ part }) => (
                      <Promotion
                        key={part.partDetails.primaryPart.itemId}
                        part={part}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}
            {/* Additional Costs */}
            <section
              className="grid gap-2 items-center mt-2"
              id="additional-costs-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>
                {t("compare.additionalCosts")}:
              </h4>
              {Boolean(visibleResults?.length) && (
                <div
                  className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                  style={{ gridColumn, gridTemplateColumns }}
                >
                  {visibleResults?.map(({ part, partQuantity }) => (
                    <AdditionalCosts
                      key={part.partDetails.primaryPart.itemId}
                      part={part}
                      partQuantity={partQuantity}
                    />
                  ))}
                </div>
              )}
            </section>
            {/* Subtotal */}
            <section
              className="grid gap-2 items-center mt-2"
              id="subtotal-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>{t("compare.subtotal")}:</h4>
              {Boolean(visibleResults?.length) && (
                <div
                  className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                  style={{ gridColumn, gridTemplateColumns }}
                >
                  {visibleResults?.map(({ part, partQuantity }) => (
                    <Subtotal
                      key={part.partDetails.primaryPart.itemId}
                      part={part}
                      partQuantity={partQuantity}
                    />
                  ))}
                </div>
              )}
            </section>
            {/* Tax */}
            <section
              className="grid gap-2 items-center mt-2"
              id="tax-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>{t("compare.tax")}:</h4>
              {Boolean(visibleResults?.length) && (
                <div
                  className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                  style={{ gridColumn, gridTemplateColumns }}
                >
                  {visibleResults?.map(({ part, partQuantity }) => (
                    <Tax
                      key={part.partDetails.primaryPart.itemId}
                      part={part}
                      partQuantity={partQuantity}
                    />
                  ))}
                </div>
              )}
            </section>
            {/* Grand Total */}
            <section
              className="grid gap-2 items-center mt-2"
              id="grand-total-section"
              style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
            >
              <h4 className={rowCaptionClass}>{t("compare.grandTotal")}:</h4>
              {Boolean(visibleResults?.length) && (
                <div
                  className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                  style={{ gridColumn, gridTemplateColumns }}
                >
                  {visibleResults?.map(({ part, partQuantity }) => (
                    <GrandTotal
                      key={part.partDetails.primaryPart.itemId}
                      part={part}
                      partQuantity={partQuantity}
                    />
                  ))}
                </div>
              )}
            </section>
            {/* Add to Quote */}
            {!printMode && (
              <section
                className="grid gap-2 items-center mt-2"
                id="add-to-quote-section"
                style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
              >
                <span className={rowCaptionClass}></span>
                {Boolean(visibleResults?.length) && (
                  <div
                    className="grid gap-2 p-4 pl-0 -ml-4 pr-0"
                    style={{ gridColumn, gridTemplateColumns }}
                  >
                    {visibleResults?.map(({ part }) => (
                      <AddToQuote
                        key={part.partDetails.primaryPart.itemId}
                        part={part}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}
            {printMode && (
              <>
                <section
                  className="grid gap-2 mt-8 print:hidden"
                  id="pricing-header-section"
                  style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
                >
                  <h3 className={clsx("md:text-[1.16em]", rowCaptionClass)}>
                    {t("compare.clientInformation")}:
                  </h3>
                </section>
                <hr className="my-1 print:hidden" />
                <section
                  className="grid gap-2 items-center mt-2"
                  id="add-to-quote-section"
                  style={{ gridTemplateColumns: gridTemplateHeaderColumns }}
                >
                  <span className={rowCaptionClass} />
                  <div
                    className="grid gap-2 bg-paper border border-edge rounded-panel-section p-4 -ml-4 pr-0"
                    style={{ gridColumn, gridTemplateColumns }}
                  >
                    <KeyValue>
                      <>{t("leadReport.customerName")}:</>
                      <>
                        {[
                          queryParams?.get("customerFirstName"),
                          queryParams?.get("customerLastName"),
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      </>
                    </KeyValue>
                    <KeyValue>
                      <>{t("leadReport.customerEmail")}:</>
                      {queryParams?.get("customerEmail")}
                    </KeyValue>
                    <KeyValue>
                      <>{t("leadReport.customerPhone")}:</>
                      {queryParams?.get("customerPhone")}
                    </KeyValue>
                  </div>
                </section>
              </>
            )}
          </>
        )}
      </div>
      {data && <div id="tw-print-ready" />}
    </div>
  );
};
